import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Box, Typography, Paper, Skeleton, Alert,
  Divider, Tooltip, IconButton, Chip, Stack,
} from '@mui/material'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { getMonthlyCost } from '../../api/client'
import type { DailyCostPoint, ServiceCostBreakdown, ResourceCostBreakdown } from '../../api/types'

const SERVICE_COLORS: Record<string, string> = {
  'virtual machines':       '#3b82f6',
  'storage':                '#8b5cf6',
  'load balancer':          '#06b6d4',
  'azure kubernetes service': '#f59e0b',
  'bandwidth':              '#10b981',
  'azure monitor':          '#ec4899',
  'azure dns':              '#f97316',
  'key vault':              '#84cc16',
}

function serviceColor(name: string, idx: number): string {
  const lower = name.toLowerCase()
  const match = Object.entries(SERVICE_COLORS).find(([k]) => lower.includes(k))
  if (match) return match[1]
  const palette = ['#3b82f6','#8b5cf6','#06b6d4','#f59e0b','#10b981','#ec4899','#f97316','#84cc16','#64748b']
  return palette[idx % palette.length]
}

function fmt(n: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 }).format(n)
}

// ── Bar chart ─────────────────────────────────────────────────────────────────

function DailyBarChart({ data, currency }: { data: DailyCostPoint[], currency: string }) {
  if (data.length === 0) return null

  const max = Math.max(...data.map(d => d.cost), 0.01)
  const W = 40
  const GAP = 4
  const CHART_H = 120
  const PAD = 8
  const totalW = data.length * (W + GAP)

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <svg width={Math.max(totalW, 300)} height={CHART_H + PAD + 24} style={{ display: 'block' }}>
        {data.map((d, i) => {
          const barH = Math.max(2, (d.cost / max) * CHART_H)
          const x = i * (W + GAP)
          const y = PAD + CHART_H - barH
          const day = d.date.slice(8, 10)
          return (
            <Tooltip key={i} title={`${d.date}: ${fmt(d.cost, currency)}`}>
              <g>
                <rect x={x} y={y} width={W} height={barH} rx={3} fill="#3b82f6" opacity={0.85} style={{ cursor: 'default' }} />
                <text x={x + W / 2} y={PAD + CHART_H + 14} textAnchor="middle" fontSize={9} fill="#94a3b8">{day}</text>
              </g>
            </Tooltip>
          )
        })}
      </svg>
    </Box>
  )
}

// ── Generic cost donut ────────────────────────────────────────────────────────

const DONUT_R = 60
const DONUT_CX = 80
const DONUT_SIZE = 160
const DONUT_STROKE = 24
const CIRCUMFERENCE = 2 * Math.PI * DONUT_R

function CostDonut({ items, label, currency }: {
  items: { name: string, cost: number }[]
  label: string
  currency: string
}) {
  const top = items.slice(0, 8)
  const total = top.reduce((s, it) => s + it.cost, 0)
  let cumulative = 0

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
      <svg width={DONUT_SIZE} height={DONUT_SIZE} viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`} style={{ flexShrink: 0 }}>
        <circle cx={DONUT_CX} cy={DONUT_CX} r={DONUT_R} fill="none" stroke="#e2e8f0" strokeWidth={DONUT_STROKE} />
        {total > 0 && top.map((it, i) => {
          const dash   = (it.cost / total) * CIRCUMFERENCE
          const gap    = CIRCUMFERENCE - dash
          const rotate = -90 + (cumulative / total) * 360
          cumulative += it.cost
          return (
            <circle key={i}
              cx={DONUT_CX} cy={DONUT_CX} r={DONUT_R}
              fill="none"
              stroke={serviceColor(it.name, i)}
              strokeWidth={DONUT_STROKE}
              strokeDasharray={`${dash} ${gap}`}
              transform={`rotate(${rotate} ${DONUT_CX} ${DONUT_CX})`}
            />
          )
        })}
        <text x={DONUT_CX} y={DONUT_CX - 4} textAnchor="middle" fontSize="13" fontWeight="700" fill="#0f172a">
          {fmt(total, currency)}
        </text>
        <text x={DONUT_CX} y={DONUT_CX + 13} textAnchor="middle" fontSize="10" fill="#64748b">{label}</text>
      </svg>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
        {top.map((it, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: serviceColor(it.name, i), flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {it.name}
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>{fmt(it.cost, currency)}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

// keep typed helpers so call sites stay readable
function serviceItems(services: ServiceCostBreakdown[]) {
  return services.map(s => ({ name: s.serviceName, cost: s.cost }))
}
function resourceItems(resources: ResourceCostBreakdown[]) {
  return resources.map(r => ({ name: r.resourceName, cost: r.cost }))
}

// ── Summary cards ─────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, sub }: { icon: React.ReactNode, label: string, value: string, sub?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, flex: '1 1 180px', minWidth: 160 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {icon}
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="h5" fontWeight={700} color="text.primary">{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

// ── Month navigator ───────────────────────────────────────────────────────────

function useMonthNav() {
  const now = new Date()
  const [year,  setYear]  = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)

  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1

  function prev() {
    if (month === 1) { setYear(y => y - 1); setMonth(12) }
    else setMonth(m => m - 1)
  }

  function next() {
    if (isCurrentMonth) return
    if (month === 12) { setYear(y => y + 1); setMonth(1) }
    else setMonth(m => m + 1)
  }

  const label = new Date(year, month - 1).toLocaleString('en-US', { month: 'long', year: 'numeric' })
  return { year, month, label, isCurrentMonth, prev, next }
}

export default function CostPage() {
  const nav = useMonthNav()
  const [selectedRgs, setSelectedRgs] = useState<string[]>([])

  // On first load selectedRgs is empty → backend uses all configured RGs.
  // After first response, show toggles; user can narrow selection.
  const { data: res, isLoading, error } = useQuery({
    queryKey: ['cost-monthly', nav.year, nav.month, selectedRgs.slice().sort().join(',')],
    queryFn: () => getMonthlyCost(nav.year, nav.month, selectedRgs.length ? selectedRgs : undefined),
    staleTime: 5 * 60_000,
  })

  const availableRgs: string[] = res?.data?.availableResourceGroups ?? []

  function toggleRg(rg: string) {
    setSelectedRgs(prev => {
      // If nothing selected → all are "active". Clicking one selects only that one.
      if (prev.length === 0) return availableRgs.filter(r => r !== rg)
      const next = prev.includes(rg) ? prev.filter(r => r !== rg) : [...prev, rg]
      // If all are selected, treat as "none selected" (all).
      return next.length === availableRgs.length ? [] : next
    })
  }

  const effectiveRgs = selectedRgs.length ? selectedRgs : availableRgs

  if (isLoading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 2 }} />
      <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
    </Box>
  )

  if (error || !res?.success || !res.data) {
    const msg = res?.error?.message ?? (error as Error)?.message ?? 'Failed to load cost data'
    return <Alert severity="error">{msg}</Alert>
  }

  const report = res.data
  const isComplete = !nav.isCurrentMonth
  const savingsPct = !isComplete && report.actualTotal > 0
    ? Math.round(((report.forecastTotal - report.actualTotal) / report.forecastTotal) * 100)
    : 0

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} mb={0.75}>Azure Cost</Typography>

          {/* Resource group toggles */}
          {availableRgs.length > 0 && (
            <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, mr: 0.5 }}>
                Resource groups:
              </Typography>
              {availableRgs.map(rg => {
                const active = effectiveRgs.includes(rg)
                return (
                  <Chip
                    key={rg}
                    label={rg}
                    size="small"
                    onClick={() => toggleRg(rg)}
                    sx={{
                      fontSize: 11, height: 22, fontFamily: 'monospace',
                      bgcolor: active ? '#dbeafe' : '#f1f5f9',
                      color: active ? '#1d4ed8' : '#94a3b8',
                      fontWeight: active ? 600 : 400,
                      border: active ? '1px solid #93c5fd' : '1px solid #e2e8f0',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: active ? '#bfdbfe' : '#e2e8f0' },
                    }}
                  />
                )
              })}
            </Stack>
          )}
        </Box>

        {/* Month navigator */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5,
          border: '1px solid #e2e8f0', borderRadius: 2, px: 0.5, bgcolor: '#fff', flexShrink: 0 }}>
          <IconButton size="small" onClick={nav.prev}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" fontWeight={600} sx={{ minWidth: 110, textAlign: 'center', userSelect: 'none' }}>
            {nav.label}
          </Typography>
          <IconButton size="small" onClick={nav.next} disabled={nav.isCurrentMonth}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Summary cards */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <StatCard
          icon={<AttachMoneyIcon sx={{ fontSize: 18, color: '#3b82f6' }} />}
          label={isComplete ? 'Total Spend' : 'MTD Spend'}
          value={fmt(report.actualTotal, report.currency)}
          sub={isComplete
            ? `${report.dailyTotals.length} days`
            : `${report.dailyTotals.length} day${report.dailyTotals.length !== 1 ? 's' : ''} so far`}
        />
        {!isComplete && (
          <StatCard
            icon={<TrendingUpIcon sx={{ fontSize: 18, color: '#f59e0b' }} />}
            label="Month Forecast"
            value={fmt(report.forecastTotal, report.currency)}
            sub={savingsPct > 0 ? `${savingsPct}% remaining` : undefined}
          />
        )}
        <StatCard
          icon={<AttachMoneyIcon sx={{ fontSize: 18, color: '#10b981' }} />}
          label="Daily Average"
          value={report.dailyTotals.length > 0
            ? fmt(report.actualTotal / report.dailyTotals.length, report.currency)
            : '—'}
        />
      </Box>

      {/* Charts row */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>

        {/* Daily bar chart */}
        <Paper variant="outlined" sx={{ p: 2.5, flex: '2 1 380px', minWidth: 280 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={2}>Daily Spend</Typography>
          {report.dailyTotals.length > 0
            ? <DailyBarChart data={report.dailyTotals} currency={report.currency} />
            : <Typography variant="body2" color="text.secondary">No data yet</Typography>}
        </Paper>

        {/* Service donut */}
        <Paper variant="outlined" sx={{ p: 2.5, flex: '1 1 280px', minWidth: 240 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={2}>By Service</Typography>
          {report.byService.length > 0
            ? <CostDonut items={serviceItems(report.byService)} label="by service" currency={report.currency} />
            : <Typography variant="body2" color="text.secondary">No data yet</Typography>}
        </Paper>

        {/* Resource donut */}
        <Paper variant="outlined" sx={{ p: 2.5, flex: '1 1 280px', minWidth: 240 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={2}>By Resource</Typography>
          {report.byResource?.length > 0
            ? <CostDonut items={resourceItems(report.byResource)} label="by resource" currency={report.currency} />
            : <Typography variant="body2" color="text.secondary">No data yet</Typography>}
        </Paper>
      </Box>

      {/* Breakdown tables */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>

        {/* Service table */}
        {report.byService.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2.5, flex: '1 1 300px' }}>
            <Typography variant="subtitle2" fontWeight={700} mb={2}>Service Breakdown</Typography>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box component="tr">
                  {['Service', 'Cost', '%'].map(h => (
                    <Box key={h} component="th" sx={{
                      textAlign: 'left', pb: 1, borderBottom: '1px solid #e2e8f0',
                      fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em',
                    }}>{h}</Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {report.byService.map((sv, i) => (
                  <Box component="tr" key={i} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                    <Box component="td" sx={{ py: 1, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: serviceColor(sv.serviceName, i), flexShrink: 0 }} />
                      <Typography variant="body2">{sv.serviceName}</Typography>
                    </Box>
                    <Box component="td" sx={{ py: 1, borderBottom: '1px solid #f1f5f9' }}>
                      <Typography variant="body2" fontWeight={600}>{fmt(sv.cost, report.currency)}</Typography>
                    </Box>
                    <Box component="td" sx={{ py: 1, borderBottom: '1px solid #f1f5f9' }}>
                      <Typography variant="body2" color="text.secondary">
                        {report.actualTotal > 0 ? `${((sv.cost / report.actualTotal) * 100).toFixed(1)}%` : '—'}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        )}

        {/* Resource table */}
        {(report.byResource?.length ?? 0) > 0 && (
          <Paper variant="outlined" sx={{ p: 2.5, flex: '1 1 300px' }}>
            <Typography variant="subtitle2" fontWeight={700} mb={2}>Resource Breakdown</Typography>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box component="tr">
                  {['Resource', 'Cost', '%'].map(h => (
                    <Box key={h} component="th" sx={{
                      textAlign: 'left', pb: 1, borderBottom: '1px solid #e2e8f0',
                      fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em',
                    }}>{h}</Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {report.byResource.map((rv, i) => (
                  <Box component="tr" key={i} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                    <Box component="td" sx={{ py: 1, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: serviceColor(rv.resourceName, i), flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{rv.resourceName}</Typography>
                    </Box>
                    <Box component="td" sx={{ py: 1, borderBottom: '1px solid #f1f5f9' }}>
                      <Typography variant="body2" fontWeight={600}>{fmt(rv.cost, report.currency)}</Typography>
                    </Box>
                    <Box component="td" sx={{ py: 1, borderBottom: '1px solid #f1f5f9' }}>
                      <Typography variant="body2" color="text.secondary">
                        {report.actualTotal > 0 ? `${((rv.cost / report.actualTotal) * 100).toFixed(1)}%` : '—'}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        )}
      </Box>

      <Divider />
      <Typography variant="caption" color="text.secondary">
        Data sourced from Azure Cost Management. Costs may be 24–48 hours delayed.
      </Typography>
    </Box>
  )
}
